import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  listWorkerTimesheets,
  submitTimesheet,
  lockTimesheet,
  reopenTimesheet,
  generateTimesheets,
  generateAllHistory,
  initTimesheet,
} from "../api/timesheets";
import type { TimesheetStatus } from "../api/types";

export function useWorkerTimesheets(
  workerUrl: string,
  status?: TimesheetStatus,
) {
  return useQuery({
    queryKey: ["tawa", "worker", "timesheets", workerUrl, status ?? null],
    queryFn: () => listWorkerTimesheets(workerUrl, status),
    enabled: !!workerUrl,
    staleTime: 30_000,
  });
}

// Feuilles du collaborateur, file de revue du manager, et calendrier semaine de
// l'équipe, qui porte le statut de la feuille (onglet Temps, panneau semaine).
function invalidateTimesheetReads(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ["tawa", "worker", "timesheets"] });
  void qc.invalidateQueries({ queryKey: ["tawa", "manager", "timesheets"] });
  void qc.invalidateQueries({ queryKey: ["tawa", "manager", "weekly"] });
}

export function useSubmitTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: submitTimesheet,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tawa", "worker", "timesheets"] });
    },
  });
}

/** Création de sa feuille pour une période (POST /workers/timesheets/init). */
export function useInitTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { workerUrl: string; start: string; end: string }) =>
      initTimesheet(p.workerUrl, p.start, p.end),
    onSuccess: () => invalidateTimesheetReads(qc),
  });
}

/** Soumission puis validation de sa propre feuille. Le verrouillage peut échouer
 * après une soumission réussie : la feuille reste alors soumise, et l'issue le
 * dit (`locked: false`) au lieu de lever, pour que l'écran l'explique. */
export function useSubmitAndLockTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (timesheetId: string) => {
      await submitTimesheet(timesheetId);
      try {
        await lockTimesheet(timesheetId);
        return { locked: true };
      } catch {
        return { locked: false };
      }
    },
    onSettled: () => invalidateTimesheetReads(qc),
  });
}

export function useLockTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: lockTimesheet,
    onSuccess: () => invalidateTimesheetReads(qc),
  });
}

/** Verrouillage en lot : pas de route groupée, une requête par feuille, en
 * série. Les échecs n'interrompent pas le lot ; la file est relue une fois. */
export function useLockTimesheets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const failed: string[] = [];
      for (const id of ids) {
        try {
          await lockTimesheet(id);
        } catch {
          failed.push(id);
        }
      }
      return { locked: ids.length - failed.length, failed };
    },
    onSettled: () => invalidateTimesheetReads(qc),
  });
}

export function useReopenTimesheet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { timesheetId: string; reason?: string }) =>
      reopenTimesheet(p.timesheetId, p.reason),
    onSuccess: () => invalidateTimesheetReads(qc),
  });
}

export function useGenerateTimesheets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { periodStart: string; periodEnd: string }) =>
      generateTimesheets(p.periodStart, p.periodEnd),
    onSuccess: () => invalidateTimesheetReads(qc),
  });
}

export function useGenerateAllHistory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => generateAllHistory(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tawa", "worker", "timesheets"] });
    },
  });
}
